<script setup lang="ts">
import ScrapingProgress from "@/components/layout/ScrapingProgress.vue";
import NotificationCenter from "@/components/notifications/NotificationCenter.vue";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";
import { useRolesStore } from "@/stores/roles";
import { computed } from "vue";
import { useRouter } from "vue-router";

const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();
const rolesStore = useRolesStore();
const router = useRouter();

const userInitials = computed(() => {
  if (!authStore.user) return "";
  return authStore.user.username.substring(0, 2).toUpperCase();
});

const userRoleName = computed(() => {
  if (!authStore.user) return "";
  const role = rolesStore.getRoleById(authStore.user.role_id);
  if (role) return role.name;
  
  // Fallback: Map role_id directly (until roles are loaded)
  const roleIdMap: Record<number, string> = {
    1: "Admin",
    2: "Moderator", 
    3: "User",
    4: "Viewer"
  };
  return roleIdMap[authStore.user.role_id] || "User";
});

const handleLogout = (): void => {
  authStore.logout();
  router.push("/login");
};
</script>

<template>
  <header class="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
    <div class="px-6 py-4">
      <div class="flex items-center justify-between">
        <!-- Logo & Title -->
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 bg-primary rounded-lg flex items-center justify-center"
          >
            <svg
              class="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div>
            <h1 class="text-xl font-bold text-gray-900">A4U Prices</h1>
            <p class="text-xs text-gray-500">V2.0</p>
          </div>
        </div>

        <!-- Center: Scraping Progress -->
        <div class="flex-1 flex justify-center px-8">
          <ScrapingProgress />
        </div>

        <!-- User Menu -->
        <div class="flex items-center gap-4">
          <!-- Notification Center -->
          <NotificationCenter />

          <!-- User Info -->
          <div class="flex items-center gap-3">
            <div class="text-right">
              <p class="text-sm font-medium text-gray-900">
                {{ authStore.user?.username }}
              </p>
              <p class="text-xs text-gray-500">
                {{ userRoleName }}
              </p>
            </div>

            <!-- User Avatar -->
            <div
              class="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center text-white font-bold"
            >
              {{ userInitials }}
            </div>
          </div>

          <!-- Logout Button -->
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            @click="handleLogout"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  </header>
</template>
